import React, { useState } from "react";

import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { LuAlertCircle, LuKeyRound } from "react-icons/lu";

import PinInput from "../pinInput";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchLoggingUserData,
  getLoggingUser,
} from "../../features/loggingUser/loggingUserSlice";
import { POST } from "../../services/axios.service";
import { toastStyles } from "../../static/styles";

interface IFormInput {
  pin: string;
  confirmPin: string;
}

const LoginPinForm = () => {
  const dispatch = useAppDispatch();
  const loggingUser = useAppSelector(getLoggingUser);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<IFormInput>({
    mode: "onSubmit",
    defaultValues: { pin: "", confirmPin: "" },
  });

  const isPinSet = !!loggingUser?.isLoginPinSet;

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);

    try {
      await POST("/v1/profile/pin", { loginPin: data.pin })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            await dispatch(fetchLoggingUserData());
            reset({ pin: "", confirmPin: "" });

            toast.success(response.data.message, { ...(toastStyles as any) });
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <div className="flex flex-col space-y-1.5 p-6">
        <div className="flex items-center justify-between gap-2">
          <div className="text-2xl font-bold text-[#113F67] leading-none tracking-tight">
            લોગિન પિન
          </div>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
              isPinSet
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            <LuKeyRound className="h-3 w-3" />
            {isPinSet ? "પિન સેટ છે" : "પિન સેટ નથી"}
          </span>
        </div>
        <div className="text-sm text-gray-600">
          ટેબ બંધ કરીને એપ ફરી ખોલતી વખતે લૉક સ્ક્રીન માટે 6 અંકનો પિન સેટ કરો.
        </div>
      </div>

      <div className="p-6 pt-0">
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label
                htmlFor="loginPinForm-pin"
                className="text-sm font-semibold text-[#113F67]"
              >
                નવો પિન <span className="text-red-500">*</span>
              </label>

              <Controller
                name="pin"
                control={control}
                rules={{
                  pattern: {
                    value: /^\d{6}$/,
                    message: "પિન 6 અંકનો હોવો જોઈએ",
                  },
                  required: "પિન 6 અંકનો હોવો જોઈએ",
                }}
                render={({ field }) => (
                  <PinInput
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isLoading}
                    slotClassName="h-11 w-9"
                  />
                )}
              />

              {errors.pin && (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.pin.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="loginPinForm-confirmPin"
                className="text-sm font-semibold text-[#113F67]"
              >
                કન્ફર્મ પિન <span className="text-red-500">*</span>
              </label>

              <Controller
                name="confirmPin"
                control={control}
                rules={{
                  required: "પિન કન્ફર્મ કરો",
                  validate: (value) =>
                    value === getValues("pin") ||
                    "પિન અને કન્ફર્મ પિન મેળ ખાતા નથી",
                }}
                render={({ field }) => (
                  <PinInput
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isLoading}
                    slotClassName="h-11 w-9"
                  />
                )}
              />

              {errors.confirmPin && (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.confirmPin.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none h-10 px-6 bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-white font-semibold text-base rounded-lg transition-all duration-300 transform hover:scale-[1.01] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  પિન સેવ થઈ રહ્યો છે...
                </div>
              ) : (
                "પિન સેવ કરો"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default LoginPinForm;
